import { useState, useRef, useEffect } from 'react'
import { HealthPlanCardContent } from '../../../api/ester-b2b-api/private/healthPlan/types'
import { MarkdownToReact } from '../../markdown/MardownToReact'
import { BodyLargeMedium, BodyXlMedium } from '../../styles/Typography'
import styled from 'styled-components'
import { theme } from '../../../theme/theme'
import { Lines } from '../../icons/lines'
import { Caret } from '../../icons'
import { useTranslation } from 'react-i18next'
import { useWindowWidth } from '../../../hooks/useWindowWidth'
import { isTablet, isMobile } from '../../../utils/devices'

export const HealthPlanCardCollapsible = ({
  content,
  isExpanded,
  onOpen,
  carePlan,
}: {
  content: HealthPlanCardContent
  isExpanded: boolean
  onOpen?: (element: HTMLElement) => void
  carePlan?: boolean
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [showCollapseButton, setShowCollapseButton] = useState<boolean>(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const width = useWindowWidth()
  const isMobileDevice = isTablet(width) || isMobile(width)
  const { t } = useTranslation()

  useEffect(() => {
    if (open && contentRef.current) {
      const threshold = isMobileDevice ? 300 : 500
      setShowCollapseButton(contentRef.current.scrollHeight > threshold)
    }
  }, [open, isMobileDevice])

  useEffect(() => {
    if (!isExpanded) {
      setOpen(false)
    }
  }, [isExpanded])

  const handleToggle = () => {
    if (!isExpanded) return

    setOpen(!open)
    if (!open && onOpen && containerRef.current) {
      setTimeout(() => {
        if (containerRef.current) {
          onOpen(containerRef.current)
        }
      }, 100)
    }
  }

  const carePlanContent = (
    <CareplanContainer $open={isExpanded}>
      <BodyLargeMedium>{content.title}</BodyLargeMedium>
      <MarkdownToReact markdown={content.text} />
    </CareplanContainer>
  )
  return (
    <Container ref={containerRef} $isExpanded={isExpanded} $isCareplan={carePlan}>
      {carePlan ? (
        carePlanContent
      ) : (
        <>
          <HeaderContainer onClick={handleToggle} $open={open}>
            <TitleContainer>
              <Lines size={20} color={isExpanded ? theme.color.plum : theme.color.grey3} />
              {isExpanded && !isMobileDevice ? (
                <BodyXlMedium>{content.title}</BodyXlMedium>
              ) : (
                <BodyLargeMedium>{content.title}</BodyLargeMedium>
              )}
            </TitleContainer>
          </HeaderContainer>
          <ContentContainer className={`${open && 'open'}`}>
            <ContentInner>
              <ContentPadding ref={contentRef}>
                <MarkdownToReact markdown={content.text} />
                {showCollapseButton && (
                  <StyledLink onClick={() => setOpen(false)}>
                    <RotatedCaret color={theme.color.plumMid} />
                    {t('employee.yourHealth.showLess')}
                  </StyledLink>
                )}
              </ContentPadding>
            </ContentInner>
          </ContentContainer>
        </>
      )}
    </Container>
  )
}

const Container = styled.div<{ $isExpanded?: boolean; $isCareplan?: boolean }>`
  padding: 0 ${({ $isCareplan }) => ($isCareplan ? theme.spacing.tiny : theme.spacing.medium)}px;
  margin: ${({ $isExpanded }) => ($isExpanded ? theme.spacing.mediumLarge : theme.spacing.small)}px 0;
`

const HeaderContainer = styled.div<{ $open: boolean; $isExpanded?: boolean }>`
  border-radius: 16px;
  color: ${({ $open }) => ($open ? theme.color.black : theme.color.grey5)};
  cursor: pointer;
`
const CareplanContainer = styled.div<{ $open: boolean }>`
  color: ${({ $open }) => ($open ? theme.color.black : theme.color.grey5)};
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${theme.spacing.mediumLarge}px;
`
const ContentContainer = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease;
  overflow: hidden;
  &.open {
    grid-template-rows: 1fr;
  }
`

const ContentInner = styled.div`
  min-height: 0;
  max-width: 1000px;
`

const ContentPadding = styled.div`
  padding: ${theme.spacing.medium}px ${theme.spacing.xxlarge}px;
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding: ${theme.spacing.medium}px 0;
  }
`
const StyledLink = styled.div`
  color: ${theme.color.plumMid};
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  cursor: pointer;
`
const RotatedCaret = styled(Caret)`
  transform: rotate(-270deg) !important;
`
